import { memo, useEffect, useState } from 'react'

import { Input } from '@/components/ui/input'
import { RadioGroupItem } from '@/components/ui/radio-group'
import { AudienceIcon } from '@/components/mediaBuying/adCampaign/AudienceIcon'
import AudienceTypeChip from '@/components/mediaBuying/adCampaign/AudienceTypeChip'
import CreativeCard from '@/pages/campaigns/creatives/components/CreativeCard'
import { Label } from '@/components/ui/label'
import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import gql from 'graphql-tag'
import { CORE_CAMPAIGN_FIELDS } from '@/graphql/campaign-fragments'
import { getCountryNameFromAddress } from '@/utils/geocoding'
import { formatCurrencyAutoLocale } from '@/utils/tools'
import { CAMPAIGN_BUDGET_AD_SET_NOTE } from '@/utils/advantage-leads'
import { PLATFORM_PLACEMENT_OPTIONS } from '@/utils/ad-campaign-constants'
import { LEAD_EVENT_TYPES, SALES_EVENT_TYPES, DEFAULT_EVENT_TYPES } from '@/utils/ad-campaign-constants'

// Helper function to generate all available placements (same logic as PlatformPlacementsSettings)
const generateAllPlacements = () => {
    return PLATFORM_PLACEMENT_OPTIONS.flatMap((platform) =>
        platform.subPlacements.map((placement) => `${platform.id}:${placement.id}`),
    )
}

const formatPlacementTargets = (placementTargets) => {
    // If no placements are selected, use all available placements as default
    const targetPlacements =
        !placementTargets || placementTargets.length === 0 ? generateAllPlacements() : placementTargets

    // Group placements by platform
    const groupedPlacements = {}

    targetPlacements.forEach((target) => {
        const [platformId, placementId] = target.split(':')
        const platform = PLATFORM_PLACEMENT_OPTIONS.find((p) => p.id === platformId)

        if (platform) {
            const placement = platform.subPlacements.find((sp) => sp.id === placementId)
            if (placement) {
                if (!groupedPlacements[platformId]) {
                    groupedPlacements[platformId] = {
                        label: platform.label,
                        placements: [],
                    }
                }
                groupedPlacements[platformId].placements.push(placement.label)
            }
        }
    })

    return groupedPlacements
}

const formatAgeRange = (ageTargets) => {
    if (!ageTargets || ageTargets.length === 0) return '18-65+'

    if (ageTargets.length === 1 && ageTargets[0].includes('+')) {
        return ageTargets[0]
    }

    const first = ageTargets[0]
    const last = ageTargets[ageTargets.length - 1]

    const minAge = first.split('-')[0]
    const maxAge = last.includes('+') ? last : last.split('-')[1]

    return `${minAge}-${maxAge}`
}

const getConversionTypeLabel = (conversionType, campaignObjective) => {
    // Determine the default conversion type based on campaign objective
    const objectiveKey = campaignObjective?.toLowerCase()
    const defaultConversionType = DEFAULT_EVENT_TYPES[objectiveKey]

    // Use the provided conversion type or fall back to default
    const actualConversionType = conversionType || defaultConversionType

    if (!actualConversionType) return '-'

    // Find the label from the event options
    const eventOptions = objectiveKey === 'sales' ? SALES_EVENT_TYPES : objectiveKey === 'leads' ? LEAD_EVENT_TYPES : []

    const option = eventOptions.find((opt) => opt.value === actualConversionType)
    return option ? option.label : actualConversionType
}

const GET_CAMPAIGN = gql`
    ${CORE_CAMPAIGN_FIELDS}
    query Campaigns($where: CampaignWhere) {
        campaigns(where: $where) {
            ...CoreCampaignFields
        }
    }
`

const Placements = ({ targets }) => {
    const formattedPlacements = formatPlacementTargets(targets)
    const platformIds = Object.keys(formattedPlacements)

    return platformIds.map((platformId) => {
        const platform = formattedPlacements[platformId]
        return (
            <p key={platformId}>
                • <strong>{platform.label}:</strong>{' '}
                <span className="tw:text-[#899CBD]">{platform.placements.join(', ')}</span>
            </p>
        )
    })
}

const Devices = ({ deviceTargets }) => {
    const hasDesktop = deviceTargets.includes('desktop')
    const hasMobileIos = deviceTargets.includes('mobile_ios')
    const hasMobileAndroid = deviceTargets.includes('mobile_android')
    const hasBothMobile = hasMobileIos && hasMobileAndroid

    return (
        <>
            {hasDesktop && <span className="tw:!pb-0 tw:block">• Desktop</span>}
            {hasBothMobile && (
                <span className="tw:!pb-0 tw:block">
                    • Mobile: <span className="tw:text-gray-200">iOS, Android</span>
                </span>
            )}
            {hasMobileIos && !hasBothMobile && (
                <span className="tw:!pb-0 tw:block">
                    • Mobile: <span className="tw:text-gray-200">iOS</span>
                </span>
            )}
            {hasMobileAndroid && !hasBothMobile && (
                <span className="tw:!pb-0 tw:block">
                    • Mobile: <span className="tw:text-gray-200">Android</span>
                </span>
            )}
        </>
    )
}

const Location = ({ locationData, campaignAddress }) => {
    const { target } = locationData
    let [countryName, setCountryName] = useState(null)

    useEffect(() => {
        const fetchCountryName = async () => {
            const countryName = await getCountryNameFromAddress(campaignAddress)
            setCountryName(countryName)
        }
        fetchCountryName()
    }, [campaignAddress])

    if (target === 'geolocation') return `${campaignAddress}. 80 km radius`
    if (target === 'countrywide') return countryName

    // worldwide
    return (
        <span>
            {locationData.included.length === 0 && <p>Worldwide.</p>}
            {locationData.included.length > 0 && (
                <p>
                    Countries: <span className="tw:text-[#899CBD]">{locationData.included.join(', ')}.</span>{' '}
                </p>
            )}
            {locationData.excluded.length > 0 && (
                <p>
                    Exclude countries:{' '}
                    <span className="tw:text-[#899CBD]">{locationData.excluded.join(', ')}.</span>{' '}
                </p>
            )}
        </span>
    )
}

const ReviewAudienceItem = memo(
    ({
        currency,
        audience,
        stage,
        creatives,
        isSelected,
        displayName,
        budget,
        usesCampaignBudget = false,
        campaignObjective,
        genderTargets,
        ageTargets,
        deviceTargets,
        locationData,
        placementTargets,
        conversionType,
        onNameChange,
    }) => {
        const params = useParams()
        const campaignId = params.id

        const { data } = useQuery(GET_CAMPAIGN, {
            variables: { where: { id: campaignId } },
        })

        const handleNameChange = (e) => {
            onNameChange(stage.id, audience.id, e.target.value)
        }

        const radioValue = `${stage.id}_${audience.id}`

        return (
            <div className="tw:group tw:relative tw:w-full tw:max-w-full">
                {/* Main audience row */}
                <Label
                    className="tw:border-l-4 tw:hover:bg-gray-500 tw:flex tw:items-center tw:justify-between tw:w-full tw:!flex"
                    htmlFor={`audience-radio-${stage.id}-${audience.id}`}
                    style={{ borderLeftColor: stage.color }}
                >
                    {/* Left side: Radio button and audience info */}
                    <div className="tw:py-4 tw:px-5 tw:flex tw:gap-3 tw:items-center tw:flex-1 tw:min-w-0">
                        {/* Radio button */}
                        <RadioGroupItem
                            value={radioValue}
                            className="tw:cursor-pointer"
                            id={`audience-radio-${stage.id}-${audience.id}`}
                        />

                        {/* Audience icon and name */}
                        <div className="tw:flex tw:items-center tw:gap-2 tw:min-w-0">
                            {audience.icon && <AudienceIcon icon={audience.icon} size="sm" />}
                            <span className="tw:text-sm tw:text-white tw:truncate">{audience.name}</span>
                            <AudienceTypeChip kind={audience.hypothesis_kind} className="tw:flex-shrink-0" />
                            {/* Campaign count next to audience name */}
                            <div className="tw:bg-[#263E57] tw:text-white tw:rounded-full tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:min-w-6 tw:text-center tw:flex-shrink-0">
                                {creatives.length}
                            </div>
                        </div>
                    </div>

                    {/* Right side: Input field */}
                    <div className="tw:flex tw:items-center tw:pr-5 tw:py-4 tw:w-[65%] tw:min-w-0">
                        <Input
                            value={displayName}
                            onChange={handleNameChange}
                            className="tw:w-full"
                            placeholder="Audience adset name"
                        />
                    </div>
                </Label>

                {/* Expanded content */}
                {isSelected && (
                    <div
                        className="tw:bg-[#071727] tw:w-full tw:max-w-full tw:overflow-hidden tw:border-l-4"
                        style={{ borderLeftColor: stage.color }}
                    >
                        <div className="tw:p-4 tw:w-full tw:max-w-full">
                            {/* Campaign Details Grid */}
                            <div className="tw:grid tw:grid-cols-3 tw:gap-6 tw:mb-6">
                                {/* Left Column */}
                                <div className="tw:space-y-4">
                                    <div>
                                        <h4 className="tw:text-white tw:!text-sm tw:!font-semibold tw:mb-1">
                                            Campaign Objective
                                        </h4>
                                        <p className="tw:text-white tw:!text-sm">{campaignObjective}</p>
                                    </div>
                                    <div>
                                        <h4 className="tw:text-white tw:!text-sm tw:!font-semibold tw:mb-1">Gender</h4>
                                        <p className="tw:text-white tw:text-sm">
                                            {genderTargets && genderTargets.length > 0
                                                ? genderTargets
                                                      .map((g) => g.charAt(0).toUpperCase() + g.slice(1))
                                                      .join(', ')
                                                : 'All'}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="tw:text-white tw:!text-sm tw:!font-semibold tw:mb-1">
                                            {audience.audience_mode === 'advantage' ? 'Age (suggestion)' : 'Age'}
                                        </h4>
                                        <p className="tw:text-white tw:text-sm">{formatAgeRange(ageTargets)}</p>
                                    </div>
                                </div>

                                {/* Middle Column */}
                                <div className="tw:space-y-4">
                                    <div>
                                        <h4 className="tw:text-white tw:!text-sm tw:!font-semibold tw:mb-1">
                                            Daily Budget
                                        </h4>
                                        <p className="tw:text-white tw:text-sm">
                                            {usesCampaignBudget
                                                ? CAMPAIGN_BUDGET_AD_SET_NOTE
                                                : formatCurrencyAutoLocale({
                                                      amount: budget,
                                                      showCode: true,
                                                      currency,
                                                  })}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="tw:text-white tw:!text-sm tw:!font-semibold tw:mb-1">
                                            Conversion Type
                                        </h4>
                                        <p className="tw:text-white tw:text-sm">
                                            {getConversionTypeLabel(conversionType, campaignObjective)}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="tw:text-white tw:!text-sm tw:!font-semibold tw:mb-1">Device</h4>
                                        <div className="tw:text-white tw:text-sm">
                                            {usesCampaignBudget ? (
                                                'Automatic'
                                            ) : (
                                                <Devices deviceTargets={deviceTargets} />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column */}
                                <div className="tw:space-y-4">
                                    <div>
                                        <h4 className="tw:text-white tw:!text-sm tw:!font-semibold tw:mb-1">
                                            Placement
                                        </h4>
                                        <div className="tw:text-white tw:text-sm">
                                            {usesCampaignBudget ? (
                                                'Automatic (Advantage+ leads)'
                                            ) : (
                                                <Placements targets={placementTargets} />
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="tw:text-white tw:!text-sm tw:!font-semibold tw:mb-1">
                                            Locations
                                        </h4>
                                        <div className="tw:text-white tw:text-sm">
                                            <Location
                                                locationData={locationData}
                                                campaignAddress={data.campaigns[0]?.address}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Ads Section */}
                            <div>
                                <h4 className="tw:text-white tw:text-sm tw:font-medium tw:mb-4">Ads</h4>
                                <div className="tw:w-full tw:overflow-x-auto" style={{ maxWidth: '100%' }}>
                                    <div className="tw:flex tw:gap-6 tw:pb-2" style={{ width: 'max-content' }}>
                                        {creatives.map((creative) => (
                                            <div key={creative.id} className="tw:flex-shrink-0">
                                                <CreativeCard
                                                    creative={creative}
                                                    isSelected={false}
                                                    onToggleSelection={() => {}}
                                                    hideCheckbox={true}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    },
)

ReviewAudienceItem.displayName = 'ReviewAudienceItem'

export default ReviewAudienceItem
